/**
 * Приём приглашения.
 *
 * Экран показывает, куда именно человека зовут и на каких условиях, до того
 * как он нажмёт «Принять». Няня должна видеть ставку заранее — узнать её
 * постфактум из расчёта было бы неуважительно.
 */

import { useQuery } from '@tanstack/react-query'

import { ApiError, api } from '@/shared/api/client'
import { useAcceptInvite, type InvitePreview } from '@/shared/api/mutations'
import { formatMoney } from '@/shared/format'
import { haptic } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Screen } from '@/shared/ui/Screen'
import { Card, Chip } from '@/shared/ui/primitives'
import { ErrorState, SkeletonCard } from '@/shared/ui/states'

export function InviteAccept({
  token,
  onDone,
  onSkip,
}: {
  token: string
  onDone: () => void
  onSkip: () => void
}) {
  const preview = useQuery<InvitePreview, ApiError>({
    queryKey: ['invite', token],
    queryFn: () => api.get<InvitePreview>(`/invites/${token}`),
    retry: false,
  })

  const accept = useAcceptInvite({
    onSuccess: () => {
      haptic('success')
      onDone()
    },
  })

  if (preview.isLoading) {
    return (
      <Screen withTabBar={false}>
        <SkeletonCard lines={4} />
      </Screen>
    )
  }

  if (preview.error) {
    return (
      <Screen withTabBar={false}>
        <ErrorState error={preview.error} />
        <Button block variant="ghost" onClick={onSkip} hapticKind="none">
          Продолжить без приглашения
        </Button>
      </Screen>
    )
  }

  const invite = preview.data!
  const isNanny = invite.role === 'nanny'

  return (
    <Screen withTabBar={false}>
      <div className="flex flex-col items-center px-2 pt-10 pb-6 text-center">
        <div
          aria-hidden
          className="bg-accent-soft mb-5 flex size-20 items-center justify-center rounded-[var(--radius-sheet)]"
        >
          <Icon name={isNanny ? 'role-nanny' : 'role-parent'} size={36} />
        </div>
        <h1 className="font-display text-title leading-tight font-extrabold">
          {invite.family_title}
        </h1>
        <p className="text-text-muted mt-2 max-w-[300px] text-body leading-snug">
          {invite.inviter_name} приглашает вас как{' '}
          {isNanny ? 'няню' : 'второго родителя'}.
        </p>
      </div>

      {isNanny && (
        <Card className="space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-text-muted text-body">Ставка</span>
            <span className="font-display text-title font-bold">
              {invite.hourly_rate ? `${formatMoney(invite.hourly_rate)} / час` : 'обсудите лично'}
            </span>
          </div>
          <p className="text-text-muted text-caption leading-snug">
            Часы считаются автоматически из смен. Расходы, которые вы понесёте, копятся и
            приходят в расчёте — напоминать о них не придётся.
          </p>
        </Card>
      )}

      <Card>
        <div className="flex items-center gap-2">
          {/* Значок отдельным элементом: внутри подписи скринридер прочёл бы
              его как часть слова. */}
          <Chip tone="accent">
            <Icon name="geo-pin" size={16} />
            геометки
          </Chip>
          <span className="text-text-muted text-caption">по вашему согласию</span>
        </div>
        <p className="text-text-muted mt-2 text-caption leading-snug">
          Координата фиксируется только в момент, когда вы сами нажимаете кнопку. Фонового
          отслеживания нет. Согласие можно отозвать в один тап в профиле.
        </p>
      </Card>

      <Button
        block
        size="lg"
        loading={accept.isPending}
        onClick={() => accept.mutate(token)}
      >
        Принять приглашение
      </Button>
      <Button block variant="ghost" onClick={onSkip} hapticKind="none">
        Не сейчас
      </Button>
    </Screen>
  )
}
